import { Play } from '@phosphor-icons/react'
import clsx from 'clsx'
import { motion, useScroll, useSpring, useTransform } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'
import { VideoBlock } from '../../@types/statamic'
import Container from '../../components/layout/Container'
import { PageInfo } from '@/src/@types/models'
import Button from '@/src/components/core/Button'
import Picture from '@/src/components/elements/Picture'

export function getTitle({ optional_title }: VideoBlock) {
  return optional_title
}

export function getImage({ thumbnail }: VideoBlock) {
  return thumbnail
}

export default function Component({
  optional_title,
  thumbnail,
  thumbnail_video,
  video,
  pageInfo,
}: VideoBlock & { pageInfo: PageInfo }) {
  const [videoClicked, setVideoClicked] = useState(false)
  const [lockedVisible, setLockedVisible] = useState(false)

  const videoContainer = useRef<HTMLDivElement>(null)

  const { scrollYProgress } = useScroll({
    target: videoContainer,
    offset: ['start 90%', 'center 60%'],
  })
  const progressRaw = useTransform(scrollYProgress, [0, 1], [0.8, 1])
  const progress = useSpring(progressRaw, {
    stiffness: 500,
    damping: 60,
  })

  useEffect(() => {
    if (lockedVisible) return

    const unsub = scrollYProgress.on('change', (v) => {
      if (v >= 1) setLockedVisible(true)
    })

    return () => unsub()
  }, [scrollYProgress, lockedVisible])

  return (
    <div
      className={clsx({
        'md:pb-12 lg:pb-16':
          !pageInfo.newSpacing && pageInfo.blueprint === 'pages',
        'mt-20 md:mt-32': pageInfo.newSpacing,
      })}
    >
      <Container>
        {optional_title && (
          <h2 className="text-center text-4xl font-black md:text-20xl">
            {optional_title}
          </h2>
        )}
        <motion.div
          ref={videoContainer}
          className="mt-6 aspect-16/9 origin-bottom overflow-hidden rounded-xl shadow-xl md:mt-10 md:rounded-3xl"
          style={
            !lockedVisible ? { scale: progress, opacity: progress } : undefined
          }
        >
          {!videoClicked && (
            <button
              onClick={() => setVideoClicked(true)}
              className="relative size-full"
            >
              {thumbnail_video && (
                <video
                  playsInline
                  autoPlay
                  muted
                  loop
                  preload="auto"
                  poster={thumbnail.permalink}
                  webkit-playsinline="true"
                  className="size-full object-cover object-center"
                >
                  <source src={thumbnail_video} type="video/webm" />
                  Your browser does not support HTML video.
                </video>
              )}
              {!thumbnail_video && thumbnail && (
                <Picture
                  {...thumbnail}
                  className="rounded-lg"
                  layout="responsive"
                />
              )}
              <Button
                as="div"
                version="white-gray"
                size="icon"
                icon={<Play weight="fill" />}
                className="animate-splash absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 md:hidden"
              />
              <div className="absolute top-1/2 left-1/2 hidden -translate-x-1/2 -translate-y-1/2 md:block">
                <div className="absolute -z-10 size-full animate-[ping_2s_cubic-bezier(0,0,0.2,1)_infinite] rounded-full bg-white opacity-50" />
                <Button
                  as="div"
                  version="white-gray"
                  size="icon-big"
                  icon={<Play weight="fill" />}
                />
              </div>
            </button>
          )}
          {videoClicked && (
            <iframe
              frameBorder="0"
              allowFullScreen
              src={`${video}?autoplay=1&origin=https://blinkdrive.ch`}
              className="size-full"
            />
          )}
        </motion.div>
      </Container>
    </div>
  )
}
